import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import type { SupabaseClient } from '../../lib/supabase';

export type JourneySubject = { table: 'prospects' | 'leads' | 'deals'; id: string };
export type Evidence = Record<string, unknown>;
export interface JourneyEvent {
  id: number;
  table_name: string;
  source_key: Evidence;
  operation: string;
  label: string;
  facts: Evidence;
  detail: Evidence;
  actor_label: string | null;
  recorded_at: string;
  /** Retained selection event matched to this exact tuple, never today's catalogue. */
  message_selection?: Evidence | null;
}
export interface JourneyVersion {
  id: string;
  fields: Evidence;
  observation: string;
}
export interface JourneyPage {
  state: 'available' | 'no_recorded_history';
  prospect_dm_count: number;
  attribution_needs_review: number;
  prospects: { id: string; label: string; dm_count: number }[];
  activity_origins: { activity_id: string; prospect_id: string; origin_count: number }[];
  events: JourneyEvent[];
  versions: JourneyVersion[];
  next_before: number | null;
  has_more: boolean;
}
export const evidenceObject = (value: unknown): value is Evidence =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const count = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const text = (value: unknown): value is string => typeof value === 'string';
export function decodeJourney(value: unknown): JourneyPage {
  if (
    !evidenceObject(value) ||
    !['available', 'no_recorded_history'].includes(String(value.state)) ||
    !count(value.prospect_dm_count) ||
    !count(value.attribution_needs_review) ||
    typeof value.has_more !== 'boolean' ||
    !(value.next_before === null || count(value.next_before)) ||
    !Array.isArray(value.prospects) ||
    !value.prospects.every(
      (p) => evidenceObject(p) && text(p.id) && text(p.label) && count(p.dm_count),
    ) ||
    !Array.isArray(value.activity_origins) ||
    !value.activity_origins.every(
      (p) =>
        evidenceObject(p) && text(p.activity_id) && text(p.prospect_id) && count(p.origin_count),
    ) ||
    !Array.isArray(value.versions) ||
    !value.versions.every(
      (v) => evidenceObject(v) && text(v.id) && text(v.observation) && evidenceObject(v.fields),
    ) ||
    !Array.isArray(value.events) ||
    !value.events.every(
      (e) =>
        evidenceObject(e) &&
        count(e.id) &&
        text(e.table_name) &&
        evidenceObject(e.source_key) &&
        text(e.operation) &&
        text(e.label) &&
        evidenceObject(e.facts) &&
        evidenceObject(e.detail) &&
        (e.actor_label === null || text(e.actor_label)) &&
        text(e.recorded_at) &&
        (e.message_selection === undefined ||
          e.message_selection === null ||
          evidenceObject(e.message_selection)),
    )
  )
    throw new Error('journey_response_invalid');
  // Project only the validated reader contract. Extra server fields are not
  // a reason to display directory data or arbitrary private rows.
  return {
    state: value.state as JourneyPage['state'],
    prospect_dm_count: value.prospect_dm_count,
    attribution_needs_review: value.attribution_needs_review,
    has_more: value.has_more,
    next_before: value.next_before,
    prospects: value.prospects as JourneyPage['prospects'],
    activity_origins: value.activity_origins as JourneyPage['activity_origins'],
    versions: value.versions as JourneyVersion[],
    events: value.events as JourneyEvent[],
  };
}
export async function fetchJourneyHistory(
  client: SupabaseClient,
  subject: JourneySubject,
  before: number | null,
) {
  const { data, error } = await client.rpc('read_journey_history', {
    p_table: subject.table,
    p_id: subject.id,
    ...(before === null ? {} : { p_before: before }),
    p_limit: 50,
  });
  if (error) throw new Error(error.message);
  return decodeJourney(data);
}
export function useJourneyHistory(subject: JourneySubject) {
  const { client, access } = useAuth();
  const actor = access?.status === 'approved' ? access.profile.id : null;
  return useInfiniteQuery({
    queryKey: ['journey-history', subject.table, subject.id, actor],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => fetchJourneyHistory(client, subject, pageParam),
    getNextPageParam: (page) => (page.has_more ? (page.next_before ?? undefined) : undefined),
    retry: false,
  });
}
